import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'

/** Next gives a repeated parameter as an array, so a caller wanting one value has to say which. */
export const firstQueryValue = (query: ParsedUrlQuery, name: string): string | undefined => {
  const value = query[name]

  return Array.isArray(value) ? value[0] : value
}

const readParam = (query: ParsedUrlQuery, name: string): string => {
  const value = firstQueryValue(query, name)
  if (value == null) throw new Error(`Route has no ${name} in its query`)

  return value
}

export const readRecordId = (query: ParsedUrlQuery): string => readParam(query, 'id')

/**
 * The record this page addresses. The route gate withholds every page until the address has resolved,
 * so a page beneath it always has one.
 */
export const useRecordId = (): string => readRecordId(useRouter().query)

export const useRouteParam = (name: string): string => readParam(useRouter().query, name)

export const useRouteParamIn = <Value extends string>(name: string, rendered: readonly Value[]): Value => {
  const value = useRouteParam(name)
  const match = rendered.find((candidate) => candidate === value)
  if (match == null) throw new Error(`Route names ${name} ${value}, which no page renders`)

  return match
}
